import { type FC, type MutableRefObject, useLayoutEffect, useRef, useState } from 'react';
import { BaseLineClose } from '@/app/components/icons/BaseLineClose';
import { cn } from '@/app/utils';

/**
 * 计算标签组中每个标签的换行情况
 * @param tags 值列表
 * @param tagRefs 标签元素的引用列表
 * @returns 包含每个标签是否需要换行的数组（可根据索引得知某个标签是否在一行的 start 或 end）
 */
const useLineBreaks = (tags: string[], tagRefs: MutableRefObject<(HTMLSpanElement | null)[]>) => {
  const [lineBreaks, setLineBreaks] = useState<{ start: boolean[]; end: boolean[] }>({
    start: Array(tags.length).fill(false),
    end: Array(tags.length).fill(false),
  });

  // 计算换行
  useLayoutEffect(() => {
    if (tags.length < 2) return;

    // 每当窗口大小变化时，重新计算换行
    const handleResize = () => {
      setTimeout(() => {
        const tagElements = tagRefs.current.filter((el) => el !== null);
        if (tagElements.length !== tags.length) return;

        const startBreaks = Array(tags.length).fill(false);
        const endBreaks = Array(tags.length).fill(false);

        // 检查每个标签是否换行
        for (let i = 1; i < tagElements.length; i++) {
          const prevElement = tagElements[i - 1];
          const currentElement = tagElements[i];

          if (prevElement && currentElement) {
            const prevRect = prevElement.getBoundingClientRect();
            const currentRect = currentElement.getBoundingClientRect();

            // 如果当前标签 top 值明显大于前一个标签（2px 的容差），就代表换行了
            if (currentRect.top > prevRect.top + 2) {
              startBreaks[i] = true;
              endBreaks[i - 1] = true;
            }
          }
        }

        setLineBreaks({ start: startBreaks, end: endBreaks });
      }, 0);
    };

    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [tags]);

  return lineBreaks;
};

interface ValuesTagProps {
  groupKey: string;
  tags?: string[];
  required?: boolean;
  isFirst?: boolean;
  isHovering?: boolean;
  onClick?: () => void;
  onDelete?: (tag: string) => void;
}

/**
 * 某个 groupKey 的一组值（tags）渲染为一个标签组
 * @param groupKey 标签组名称
 * @param tags 值列表
 * @param required groupKey 是否必填
 * @param isFirst 是否是第一个标签组
 * @param isHovering 是否 hover 整个标签组
 * @param onClick 点击标签组时的回调
 * @param onDelete 删除某个标签时的回调
 * @returns
 */
export const ValuesTag: FC<ValuesTagProps> = ({
  groupKey,
  tags = [],
  required,
  isFirst,
  isHovering,
  onClick,
  onDelete,
}) => {
  const tagRefs = useRef<(HTMLSpanElement | null)[]>([]);
  const lineBreaks = useLineBreaks(tags, tagRefs);

  if (tags.length === 0) return null;

  return (
    <>
      {!isFirst && <div className="mr-1 self-end text-system-white36 text-xl">,</div>}
      {tags.map((tag, index) => (
        <span key={`${groupKey}-${tag}`} className="relative">
          {/* hover 显示字段名 */}
          {index === 0 && isHovering && (
            <>
              <span className="-top-5 absolute left-0 flex h-5 items-center whitespace-nowrap rounded-t-8 rounded-r-8 bg-system-bg02 px-2 text-system-white64 text-xs leading-5">
                {groupKey}
                {required && <span className="ms-1 text-system-danger/[.64] text-xs">*</span>}
              </span>
              <span className="absolute top-0 left-0 size-3 bg-system-bg02" />
            </>
          )}

          {/* 标签组块样式 */}
          <span
            className={cn(
              'group relative inline-flex cursor-pointer items-center bg-[var(--tag-color)] py-1 text-base text-white leading-5',
              (index === 0 || lineBreaks.start[index]) && 'rounded-l-8 pl-2',
              lineBreaks.end[index] && 'rounded-r-8',
              index === tags.length - 1 && 'mr-1 rounded-r-8 pr-2',
              isHovering && 'z-10 backdrop-blur-sm',
            )}
            data-tag-group={groupKey}
            onClick={onClick}
            style={
              {
                '--tag-color': isHovering ? 'rgba(0, 134, 255, 0.80)' : 'rgba(0, 134, 255, 0.24)',
              } as React.CSSProperties
            }
            ref={(el) => {
              tagRefs.current[index] = el;
            }}
          >
            {tag}

            {/* 删除按钮 */}
            <div className="absolute top-1 right-2 hidden h-5 w-8 bg-gradient-to-l from-[#0470D2] to-transparent group-hover:block">
              <button
                type="button"
                className="-translate-y-1/2 absolute top-1/2 right-0 flex size-5 items-center justify-center rounded-full bg-system-black24 backdrop-blur-[1.67px]"
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(tag);
                }}
              >
                <BaseLineClose className="text-xs" />
              </button>
            </div>

            {index !== tags.length - 1 && <span className="mx-1 text-system-white36 text-xl leading-5">,</span>}
          </span>
        </span>
      ))}
    </>
  );
};
